import { useParams } from 'react-router-dom'
import { useAbruf } from '../api'
import { Angabe, Karte, Merkmal, Tabelle, Zustand } from '../bausteine'
import { datum, einheit, geld, menge, preisbasis, zahl } from '../formate'

interface Positionszeile {
  nummer: number
  art: string
  bezeichnung: string
  zeitraumVon: string | null
  zeitraumBis: string | null
  menge: string | null
  einheit: string | null
  preis: string | null
  preisbasis: string | null
  betragNetto: string
  steuersatz: string | null
  bestandteil: string | null
}

interface Rechnungsblatt {
  rechnungsnummer: string
  art: string
  status: string
  marktlokation: string | null
  rechnungsdatum: string
  faelligAm: string
  zeitraumVon: string
  zeitraumBis: string
  produktbezeichnung: string | null
  verbrauchKwh: string | null
  vorjahresverbrauchKwh: string | null
  ermittlungsmethode: string | null
  zaehlernummer: string | null
  zaehlerstandAnfang: string | null
  zaehlerstandEnde: string | null
  nettobetrag: string
  steuerbetrag: string
  bruttobetrag: string
  verrechneteAbschlaege: string
  zahlbetrag: string
  belegSchluessel: string | null
  positionen: Positionszeile[]
}

export default function Rechnungsblatt() {
  const { rechnungsnummer } = useParams()
  const blatt = useAbruf<Rechnungsblatt>(`/api/rechnungen/${rechnungsnummer}`)

  return (
    <>
      <h1>Rechnung {rechnungsnummer}</h1>
      <p className="untertitel">
        {blatt.daten?.produktbezeichnung} · {datum(blatt.daten?.zeitraumVon)} bis{' '}
        {datum(blatt.daten?.zeitraumBis)}
      </p>

      <Zustand laeuft={blatt.laeuft} fehler={blatt.fehler}>
        {blatt.daten && (
          <>
            <Karte titel="Kopfdaten">
              <div className="angaben">
                <Angabe beschriftung="Art">{blatt.daten.art}</Angabe>
                <Angabe beschriftung="Status">
                  <Merkmal art={blatt.daten.status === 'STORNIERT' ? 'fehler' : 'gut'}>
                    {blatt.daten.status}
                  </Merkmal>
                </Angabe>
                <Angabe beschriftung="Rechnungsdatum">
                  {datum(blatt.daten.rechnungsdatum)}
                </Angabe>
                <Angabe beschriftung="Fällig am">{datum(blatt.daten.faelligAm)}</Angabe>
                <Angabe beschriftung="Marktlokation">
                  {blatt.daten.marktlokation ?? '—'}
                </Angabe>
                <Angabe beschriftung="Zählernummer">
                  {blatt.daten.zaehlernummer ?? '—'}
                </Angabe>
              </div>
            </Karte>

            <Karte titel="Verbrauch">
              <div className="angaben">
                <Angabe beschriftung="Verbrauch">{menge(blatt.daten.verbrauchKwh)}</Angabe>
                <Angabe beschriftung="Vorjahresverbrauch">
                  {menge(blatt.daten.vorjahresverbrauchKwh)}
                </Angabe>
                <Angabe beschriftung="Ermittlung">
                  {blatt.daten.ermittlungsmethode ?? '—'}
                </Angabe>
                <Angabe beschriftung="Zählerstand Anfang">
                  {zahl(blatt.daten.zaehlerstandAnfang)}
                </Angabe>
                <Angabe beschriftung="Zählerstand Ende">
                  {zahl(blatt.daten.zaehlerstandEnde)}
                </Angabe>
              </div>
            </Karte>

            <Karte titel="Positionen">
              <Tabelle
                spalten={[
                  { name: 'Nr.', zahl: true },
                  { name: 'Bezeichnung' },
                  { name: 'Zeitraum' },
                  { name: 'Menge', zahl: true },
                  { name: 'Preis', zahl: true },
                  { name: 'Netto', zahl: true },
                  { name: 'USt.', zahl: true },
                ]}
              >
                {blatt.daten.positionen.map((position) => (
                  <tr key={position.nummer}>
                    <td className="zahl">{position.nummer}</td>
                    <td>
                      {position.bezeichnung}
                      {position.bestandteil && (
                        <div style={{ fontSize: 12, color: 'var(--gedaempft)' }}>
                          {position.bestandteil}
                        </div>
                      )}
                    </td>
                    <td>
                      {position.zeitraumVon
                        ? `${datum(position.zeitraumVon)} – ${datum(position.zeitraumBis)}`
                        : '—'}
                    </td>
                    <td className="zahl">
                      {position.menge
                        ? `${zahl(position.menge)} ${einheit(position.einheit)}`
                        : '—'}
                    </td>
                    <td className="zahl">
                      {position.preis
                        ? `${zahl(position.preis, 4)} ${preisbasis(position.preisbasis)}`
                        : '—'}
                    </td>
                    <td className="zahl">{geld(position.betragNetto)}</td>
                    <td className="zahl">
                      {/* Eine Abschlagsverrechnung ist ein Bruttoabzug und traegt
                          keinen eigenen Steuersatz. */}
                      {position.art === 'ABSCHLAGSVERRECHNUNG'
                        ? '—'
                        : `${zahl(position.steuersatz, 1)} %`}
                    </td>
                  </tr>
                ))}
              </Tabelle>
            </Karte>

            <Karte titel="Summen">
              <div className="angaben">
                <Angabe beschriftung="Netto">{geld(blatt.daten.nettobetrag)}</Angabe>
                <Angabe beschriftung="Umsatzsteuer">{geld(blatt.daten.steuerbetrag)}</Angabe>
                <Angabe beschriftung="Brutto">{geld(blatt.daten.bruttobetrag)}</Angabe>
                <Angabe beschriftung="Verrechnete Abschläge">
                  {geld(blatt.daten.verrechneteAbschlaege)}
                </Angabe>
                <Angabe beschriftung="Zahlbetrag">
                  <strong>{geld(blatt.daten.zahlbetrag)}</strong>
                </Angabe>
              </div>
            </Karte>
          </>
        )}
      </Zustand>
    </>
  )
}
